import { useEffect, useState } from 'react';
import { AlertTriangle, X } from 'lucide-react';
import { Button } from '../ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const DISMISS_KEY = 'cb_deploy_alert_dismissed_until';

/**
 * Amber "stale build" banner shown across the Owner Admin when the backend
 * has been running > DEPLOY_STALE_THRESHOLD_H hours since its last redeploy.
 *
 * Surfaces the most common "I tested in preview but prod is stale" pitfall
 * before debugging customer-reported issues.
 *
 * Dismissible — choice persists for 12h via localStorage so the banner
 * doesn't nag the operator constantly between mini-debug sessions.
 */
export default function DeployAlertBanner() {
  const [info, setInfo] = useState(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    // Honor a recent dismissal
    try {
      const dismissedUntil = parseInt(localStorage.getItem(DISMISS_KEY) || '0', 10);
      if (Date.now() < dismissedUntil) setDismissed(true);
    } catch {
      /* localStorage unavailable */
    }

    let cancelled = false;
    const fetchOnce = async () => {
      try {
        const r = await fetch(`${API_URL}/api/owner-admin/cost-dashboard/deploy-info`, {
          credentials: 'include',
        });
        if (!r.ok) return;
        const data = await r.json();
        if (!cancelled) setInfo(data);
      } catch {
        /* silent */
      }
    };
    fetchOnce();
    const t = setInterval(fetchOnce, 300000); // 5 min
    return () => {
      cancelled = true;
      clearInterval(t);
    };
  }, []);

  if (!info?.is_stale || dismissed) return null;

  const hours = info.stale_hours || 0;
  const days = hours / 24;
  const ageLabel =
    days >= 1
      ? `${days.toFixed(1).replace(/\.0$/, '')}d`
      : `${Math.round(hours)}h`;

  const onDismiss = () => {
    setDismissed(true);
    try {
      localStorage.setItem(DISMISS_KEY, String(Date.now() + 12 * 60 * 60 * 1000));
    } catch {
      /* ignore */
    }
  };

  return (
    <div
      className="border-b border-amber-500/30 bg-amber-500/10 text-amber-900 dark:text-amber-200 backdrop-blur-xl"
      data-testid="deploy-alert-banner"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-2.5 flex items-center gap-3">
        <AlertTriangle className="w-4 h-4 flex-shrink-0" />
        <p className="text-xs sm:text-sm flex-1 min-w-0">
          <span className="font-semibold uppercase tracking-wider text-[10px] sm:text-xs">
            Stale build
          </span>
          <span className="mx-2 text-amber-700/60 dark:text-amber-400/60">·</span>
          This <span className="font-mono">{info.environment}</span> backend has been
          running for{' '}
          <span data-testid="deploy-alert-age" className="font-semibold">
            {ageLabel}
          </span>{' '}
          without a redeploy. If you're debugging a recent change, push a new build
          before testing — the preview pane may not reflect what's live.
        </p>
        <Button
          size="sm"
          variant="ghost"
          onClick={onDismiss}
          className="h-7 w-7 p-0 text-amber-900 dark:text-amber-200 hover:bg-amber-500/20"
          aria-label="Dismiss"
          data-testid="deploy-alert-dismiss"
        >
          <X className="w-3.5 h-3.5" />
        </Button>
      </div>
    </div>
  );
}
